/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { get, groupBy, initial, orderBy } from 'lodash';
import { getPathsToProperty, getPathsToPropertyAsArray, getPathsToPropertyWithValue } from '../utils/objectUtils';
import { uiSchemaProperties } from '../enums/uiSchemaProperties';
import { mapEntityFields } from './entityFieldsMapper';
import { SubmissionSectionType } from '../../components/common/SummaryCard/CardBody/SubmissionSectionType';

/**
 * Collects all kind=party items in the submission using provided uiSchema
 * @returns array of parties
 */
export function getFlattenedParties(submission = {}, uiSchema = {}) {
  const partiesPaths = getPathsToPropertyWithValue(uiSchema, uiSchemaProperties.KIND, 'party');
  return partiesPaths.reduce((acc, path) => {
    const parties = (get(submission, path) || []).map(party => ({ ...party, path }));
    return [...acc, ...(parties || [])];
  }, []);
}

function getAllSectionsWithUiSummary(uiSchema) {
  const allPaths = getPathsToPropertyAsArray(uiSchema, uiSchemaProperties.UI_SUMMARY);
  return groupBy(allPaths, p => p[0]);
}

function createEntriesForArray(entityArray = [], entityTitleSingular, jsonSchema, uiSchema, pathToSection) {
  return entityArray
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    .map((entity, index) => {
      const showNumber = entityArray.length > 1;
      const pathToUiSummary = pathToSection.concat('.').concat(uiSchemaProperties.UI_SUMMARY);
      const summaryConfig = get(uiSchema, pathToUiSummary) || {};
      const { cardTitle: value } = mapEntityFields(summaryConfig, entity, jsonSchema);
      const title = entity.sequenceNumber
        ? `${entityTitleSingular} ${showNumber ? index + 1 : ''}`
        : entityTitleSingular;
      const { id } = entity;
      return { title, value, id };
    });
}

export function getSubmissionSummaryContent(submission = {}, uiSchema = {}, t, i18n, schema) {
  let maxVisibleParties = 4;
  const summaryContent = {};

  const sections = getAllSectionsWithUiSummary(uiSchema);

  Object.entries(sections).forEach(([sectionName, pathSegments]) => {
    let cardEntries = [];
    pathSegments.forEach((segment) => {
      const pathToSection = initial(segment).join('.');
      const sectionUiSchema = get(uiSchema, pathToSection) || {};
      const titleSingular = sectionUiSchema[uiSchemaProperties.UI_TITLE_SINGULAR];
      const submissionEntries = get(submission, pathToSection) || [];

      if (!Array.isArray(submissionEntries)) {
        const pathToUiSummary = pathToSection.concat('.').concat(uiSchemaProperties.UI_SUMMARY);
        const uiSummaryUiSchema = get(uiSchema, pathToUiSummary) || {};
        const { entries: valueEntries } = mapEntityFields(uiSummaryUiSchema, submissionEntries, schema);
        cardEntries = cardEntries.concat(valueEntries);
      } else {
        const arrayEntries = createEntriesForArray(submissionEntries, titleSingular, schema, uiSchema, pathToSection);

        if (
          sectionName === SubmissionSectionType.PARTIES ||
          sectionName === SubmissionSectionType.SEQLBIO ||
          sectionName === SubmissionSectionType.DECLARATIONS
        ) {
          if (arrayEntries.length > 0) {
            const extraText = t('summary.see.all.'.concat(pathToSection));
            const section = {
              extraText,
              items: [],
              extra: [],
              sectionHeader:
                sectionUiSchema[uiSchemaProperties.UI_TITLE] || sectionUiSchema[uiSchemaProperties.UI_TITLE_SINGULAR],
              type: sectionName,
              sectionType: segment[1],
              initialVisibleItems: sectionUiSchema[uiSchemaProperties.UI_SUMMARY_INITIAL_ITEMS],
            };
            section.items.push(arrayEntries.shift());
            section.extra = arrayEntries;
            maxVisibleParties -= 1;
            cardEntries.push(section);
          }
        } else {
          cardEntries = cardEntries.concat(arrayEntries);
        }
      }
    });

    if (sectionName === SubmissionSectionType.PARTIES) {
      const partiesPreferenceOrder = ['applicant', 'representative', 'employee', 'inventor'];
      partiesPreferenceOrder.forEach((section) => {
        const selectedSection = getSectionByType(cardEntries, section);
        if (selectedSection) {
          while (maxVisibleParties > 0 && selectedSection.extra.length > 0) {
            selectedSection.items.push(selectedSection.extra.shift());
            maxVisibleParties -= 1;
          }
          if (selectedSection.extra.length > 0) {
            selectedSection.seeMore = true;
          }
        }
      });
    }
    summaryContent[sectionName] = cardEntries;
  });

  return summaryContent;
}

export function getScrollSections(uiSchema = {}) {
  return getPathsToProperty(uiSchema, 'scrollSection').map(s => s.substring(0, s.lastIndexOf('.')));
}

/**
 * Extracts the submission title as the value of the fist submission property marked in the UISCHEMA with
 * {@link uiSchemaProperties.TITLE_FIELD} as <b>true</b>.
 * @param submission submission data
 * @param uiSchema uiSchema used to render the submssion data in the form
 * @returns {any|string}
 */
export function getSubmissionTitle(submission = {}, uiSchema = {}) {
  const titleFields = getPathsToPropertyAsArray(uiSchema, uiSchemaProperties.TITLE_FIELD, true);
  if (submission && titleFields && titleFields.length) {
    const propPath = titleFields[0].join('.');
    return get(submission, propPath);
  }
  return '';
}

function getFirstApplicant(submission) {
  return (
    submission &&
    submission
      .map(s => s?.parties)
      .map(p => p?.applicants)
      .map(m => orderBy(m, ['sequenceNumber'], ['asc']))
      .find(a => (a ? a[0]?.sequenceNumber === 1 : false))
  );
}

function getApplicantName(firstApplicant, isPartyCompany, translate) {
  let name = '';
  if (isPartyCompany(firstApplicant.role)) {
    name = translate(firstApplicant?.companyDetails?.company);
  } else {
    const firstName = firstApplicant?.personalDetails?.firstName ? firstApplicant.personalDetails?.firstName : '';
    const lastName = firstApplicant?.personalDetails?.lastName ? firstApplicant.personalDetails?.lastName : '';
    name = `${firstName} ${lastName}`;
  }
  return name;
}

function getSectionByType(sections, sectionHeader) {
  return sections.find(section => section?.sectionHeader?.toLowerCase() === sectionHeader);
}

export function getFirstApplicantName(submission, isPartyCompany, translate) {
  const applicant = getFirstApplicant(submission);
  return applicant ? getApplicantName(applicant[0], isPartyCompany, translate) : '';
}

export function getTableFilterColumnAccessors() {
  return ['basicFilingInfo.userReference', 'basicFilingInfo.titleOfInvention'];
}

export function getUserReference(submission) {
  return submission?.basicFilingInfo?.userReference;
}

export function getSubmissionErrors(submission) {
  return submission?.errors?.filter(error => error != null);
}

export const getSubmissionErrorMessages = (submission) => {
  return getSubmissionErrors(submission)
    ?.map(error => error.description)
    .join('\n');
};
